import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../lib/api';

const sortPoints = (list) =>
  [...list].sort((a, b) => (a.order - b.order) || (a.id - b.id));

/** CRUD for an idea's points, with optimistic updates and rollback on failure. */
export function usePoints(ideaId) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!ideaId) return;
    setLoading(true);
    try {
      // Tolerate both a bare array and a DRF paginated envelope.
      const res = await api.get(`/ideas/${ideaId}/points/`);
      setPoints(sortPoints(res.data?.results || res.data || []));
    } catch {
      toast.error('Failed to load points');
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    load();
  }, [load]);

  const addPoint = useCallback(
    async (text, stance = 'neutral') => {
      const clean = text.trim();
      if (!clean) return;
      // Same tmp- convention useChat uses for optimistic ids.
      const tempId = `tmp-${Date.now()}`;
      const optimistic = {
        id: tempId,
        text: clean,
        source: 'user',
        stance,
        order: points.length,
        isNew: true,
      };
      setPoints((prev) => [...prev, optimistic]);
      try {
        const res = await api.post(`/ideas/${ideaId}/points/`, {
          text: clean,
          stance,
        });
        setPoints((prev) =>
          sortPoints(prev.map((p) => (p.id === tempId ? res.data : p)))
        );
      } catch {
        setPoints((prev) => prev.filter((p) => p.id !== tempId));
        toast.error('Failed to add point');
      }
    },
    [ideaId, points.length]
  );

  const editPoint = useCallback(
    async (pointId, changes) => {
      const previous = points;
      setPoints((prev) =>
        prev.map((p) => (p.id === pointId ? { ...p, ...changes } : p))
      );
      try {
        await api.patch(`/ideas/${ideaId}/points/${pointId}/`, changes);
      } catch {
        setPoints(previous);
        toast.error('Failed to update point');
      }
    },
    [ideaId, points]
  );

  const deletePoint = useCallback(
    async (pointId) => {
      const previous = points;
      setPoints((prev) => prev.filter((p) => p.id !== pointId));
      try {
        await api.delete(`/ideas/${ideaId}/points/${pointId}/`);
      } catch {
        setPoints(previous);
        toast.error('Failed to delete point');
      }
    },
    [ideaId, points]
  );

  const movePoint = useCallback(
    async (pointId, delta) => {
      const index = points.findIndex((p) => p.id === pointId);
      const target = index + delta;
      if (index === -1 || target < 0 || target >= points.length) return;

      const reordered = [...points];
      [reordered[index], reordered[target]] = [
        reordered[target],
        reordered[index],
      ];
      const withOrder = reordered.map((p, i) => ({ ...p, order: i }));

      const previous = points;
      setPoints(withOrder);
      try {
        await api.post(`/ideas/${ideaId}/points/reorder/`, {
          order: withOrder.map((p) => p.id),
        });
      } catch {
        setPoints(previous);
        toast.error('Failed to reorder points');
      }
    },
    [ideaId, points]
  );

  return { points, loading, addPoint, editPoint, deletePoint, movePoint, reload: load };
}
